import * as Communicator from "@ts3d-hoops/web-viewer";
import { SettingState } from "common/define";
import { GlobalState } from "common/global";
import { GhostDrawMode, OperatorType } from "common/type-viewer";
import Utils from "utils/utils";
import { selectionFlag } from "../extends/CSelectionManager";
import ModelHelper from "../model/model.helper";
import AreaSelectOperator from "./areaSelect.operator";
import BaseOperator from "./base.operator";
import OrbitOperator from "./orbit.operator";
import PanOperator from "./pan.operator";
import SelectOperator from "./select.operator";
import TurntableOperator from "./turntable.operator";
import WalkOperator from "./walk.operator";
import ZoomWindowOperator from "./zoom-window.operator";

export const mapOperatorTypeDrawMode: Partial<
    Record<OperatorType, Communicator.DrawMode>
> = {
    "drawMode-wireframe": Communicator.DrawMode.Wireframe,
    "drawMode-shaded": Communicator.DrawMode.Shaded,
    "drawMode-shaded-wire": Communicator.DrawMode.WireframeOnShaded,
    "drawMode-hidden-line": Communicator.DrawMode.HiddenLine,
    "drawMode-xray": Communicator.DrawMode.XRay,
    "drawMode-ghosting": GhostDrawMode,
};
export const mapDrawModeOperator: Partial<
    Record<Communicator.DrawMode, OperatorType>
> = {
    0: "drawMode-wireframe",
    1: "drawMode-shaded",
    2: "drawMode-shaded-wire",
    3: "drawMode-hidden-line",
    4: "drawMode-xray",
    10001: "drawMode-ghosting",
};

export class SetOperator {
    viewer: Communicator.IWebViewer | undefined;
    get currentOperator(): BaseOperator | undefined {
        return this._currentOperator;
    }
    set currentOperator(newOperator: BaseOperator | undefined) {
        GlobalState.currentActiveOperator$.next(newOperator);
        this.setCurrentOperator(newOperator);
    }

    constructor(viewer: Communicator.IWebViewer | undefined) {
        this.viewer = viewer;
    }

    callFunctionHome(): boolean {
        if (this.viewer) {
            this.resetHome();
            return true;
        }
        return false;
    }
    resetModel(): boolean {
        if (this.viewer) {
            const drawMode = GlobalState.mapInitDrawMode.get(this.viewer);
            if (drawMode) {
                const opeDrawMode = mapDrawModeOperator[drawMode];
                !this.viewer.sheetManager.isDrawingSheetActive() &&
                    opeDrawMode &&
                    this.setOperatorDrawMode(opeDrawMode);
                // this.viewer.selectPart(null);
                return true;
            }
        }
        return false;
    }
    showAll(type: OperatorType): boolean {
        if (this.viewer) {
            !this.viewer.sheetManager.isDrawingSheetActive() &&
                this.setOperatorDrawMode(type);
            return true;
        }
        return false;
    }
    setPanOperator(): boolean {
        if (this.viewer) {
            this.currentOperator = new PanOperator(this.viewer);
            return true;
        }
        return false;
    }

    setOrbitOperator(viewId: string): boolean {
        if (this.viewer) {
            this.currentOperator = new OrbitOperator(this.viewer, viewId);
            return true;
        }
        return false;
    }

    setOperatorSelect(viewId: ViewId): boolean {
        if (this.viewer) {
            this.currentOperator = new SelectOperator(this.viewer, viewId);
            return true;
        }
        return false;
    }

    setOperatorAreaSelect(viewId: ViewId): boolean {
        if (this.viewer) {
            this.currentOperator = new AreaSelectOperator(this.viewer, viewId);
            return true;
        }
        return false;
    }

    setOperatorZoomIn(): boolean {
        this.viewer && zoomOperator("in", this.viewer);
        return false;
    }
    setOperatorZoomOut(): boolean {
        this.viewer && zoomOperator("out", this.viewer);
        return false;
    }
    setOperatorZoomWindow(): boolean {
        if (this.viewer) {
            this.currentOperator = new ZoomWindowOperator(this.viewer);
            return true;
        }
        return false;
    }
    setOperatorZoomFit(): boolean {
        if (this.viewer) {
            ModelHelper.zoomFit(this.viewer);
        }
        return false;
    }
    setOperatorRotateZ(): boolean {
        if (this.viewer) {
            this.currentOperator = new TurntableOperator(
                this.viewer,
                Communicator.Axis.Z
            );
            return true;
        }
        return false;
    }
    setOperatorWalkThrough(): boolean {
        if (this.viewer) {
            this.currentOperator = new WalkOperator(this.viewer);
            this.viewer.selectionManager.clear();
            this.viewer.getViewElement().focus();
            return true;
        }
        return false;
    }
    setOperatorDrawMode(
        type: OperatorType,
        isSelectInTree = false,
        selectedNodes: number[] = [],
        triggerSelect = true
    ): boolean {
        if (this.viewer) {
            setDrawMode(
                this.viewer,
                type,
                isSelectInTree,
                selectedNodes,
                triggerSelect
            );
            return true;
        }
        return false;
    }
    escape(viewId: string): void {
        if (this._currentOperator) {
            this._currentOperator.Escape();
            this._currentOperator.InActive();
        }
        this.setOperatorSelect(viewId);
    }
    checkDefault(): boolean {
        return this._currentOperator instanceof SelectOperator;
    }

    /** private */
    private _currentOperator: BaseOperator | undefined;
    private setCurrentOperator(newOperator: BaseOperator | undefined) {
        if (newOperator !== this._currentOperator && newOperator) {
            if (this._currentOperator) {
                this._currentOperator.Escape();
                this._currentOperator.InActive();
            }
            newOperator.Active();
            this._currentOperator = newOperator;
        }
    }
    private async resetHome() {
        if (this.viewer?.sheetManager.isDrawingSheetActive()) {
            this.viewer.view.setProjectionMode(
                Communicator.Projection.Orthographic
            );
            this.viewer.fitWorld();
        } else {
            // await this.viewer?.view.resetCamera(Communicator.DefaultTransitionDuration);
            await this.viewer?.view.setViewOrientation(
                Communicator.ViewOrientation.Iso
            );
        }
    }
}

/** functions */
function zoomOperator(
    type: "in" | "out",
    viewer: Communicator.IWebViewer
): void {
    const delta = type === "out" ? -1 : 1;
    const cs = viewer.view.getCanvasSize();
    const even = new Communicator.Event.MouseWheelInputEvent(
        cs.x / 2,
        cs.y / 2,
        delta,
        Communicator.Buttons.None,
        Communicator.KeyModifiers.None,
        Communicator.MouseInputType.Down,
        viewer.view.id
    );
    viewer.view.operatorManager
        .getOperator(Communicator.OperatorId.Zoom)
        .onMousewheel(even);
}

function updateXRayGhostOpacity(viewer: Communicator.IWebViewer) {
    const currXRayOpacity = viewer.view.xrayOpacity;
    const currGhostOpacity = viewer.view.ghostOpacity;
    const setting: SettingState | null = Utils.getValueLocalStorage("setting");
    const xRayOpacity = setting ? setting.general.xRayOpacity : currXRayOpacity;
    const ghostOpacity = setting
        ? setting.general.ghostingOpacity
        : currGhostOpacity;
    ModelHelper.applyXrayOpacity(viewer, xRayOpacity, ghostOpacity);
}

function setDrawMode(
    viewer: Communicator.IWebViewer,
    type: OperatorType,
    isSelectInTree = false,
    selectedNodes: number[] = [],
    triggerSelect = true
) {
    const mapOperatorTypeDrawMode: Partial<
        Record<OperatorType, Communicator.DrawMode>
    > = {
        "drawMode-wireframe": Communicator.DrawMode.Wireframe,
        "drawMode-shaded": Communicator.DrawMode.Shaded,
        "drawMode-shaded-wire": Communicator.DrawMode.WireframeOnShaded,
        "drawMode-hidden-line": Communicator.DrawMode.HiddenLine,
        "drawMode-xray": Communicator.DrawMode.XRay,
        "drawMode-ghosting": GhostDrawMode,
    };
    const mode = mapOperatorTypeDrawMode[type];
    const currentMode = viewer.view.getDrawMode();
    if (currentMode === mode) return;
    if (mode !== undefined) {
        const rootNodeId = ModelHelper.getRootNodeId(viewer);
        const flags = viewer.selectionManager.getFlag();
        if (flags.includes(selectionFlag.SelectByIsolate) && rootNodeId) {
            viewer.selectionManager.removeFlag(selectionFlag.SelectByIsolate);
            viewer.model.setInstanceModifier(
                Communicator.InstanceModifier.DoNotSelect,
                [rootNodeId],
                false
            );
        }
        if (mode !== Communicator.DrawMode.XRay && mode !== GhostDrawMode) {
            viewer.view.setDrawMode(mode as Communicator.DrawMode);
            if (mode === Communicator.DrawMode.HiddenLine) {
                const { top, bottom } = viewer.view.getBackgroundColor();
                ModelHelper.configHiddenLineMode(viewer, top, bottom);
            }
        } else {
            // const rootNodeId = ModelHelper.getRootNodeId(viewer);
            if (rootNodeId !== null) {
                if (mode === GhostDrawMode) {
                    if (selectedNodes.length === 0) {
                        const selectionItems =
                            ModelHelper.getSelectionItemId(viewer);
                        if (triggerSelect)
                            selectionItems.forEach((node) =>
                                viewer.selectPart(
                                    node,
                                    Communicator.SelectionMode.Add
                                )
                            );
                        else
                            viewer.selectionManager.addNodeSelectWithoutTrigger(
                                selectionItems
                            );
                    } else {
                        if (triggerSelect)
                            selectedNodes.forEach((node) =>
                                viewer.selectPart(
                                    node,
                                    Communicator.SelectionMode.Set
                                )
                            );
                        else
                            viewer.selectionManager.addNodeSelectWithoutTrigger(
                                selectedNodes
                            );
                    }
                    ModelHelper.unGhostingNodes(viewer);
                } else {
                    viewer.model.setInstanceModifier(
                        Communicator.InstanceModifier.DoNotXRay,
                        selectedNodes,
                        true
                    );
                }
                viewer.view.setDrawMode(mode as Communicator.DrawMode);
            }
        }
        GlobalState.mapCurrentDrawMode.set(viewer, type);
        // if (!isSelectInTree) GlobalState.mapTempDrawMode.set(viewer, type);
    }
    updateXRayGhostOpacity(viewer);
}
